import React from 'react';
import { observer } from 'mobx-react';
import Alert from 'react-s-alert';

import { analytics } from 'core/analytics';
import { routerStore, Link } from 'core/routing';

export const floorNumberTo = (number, to) => {
  return Math.floor(number / to) * to;
};

export const loadExternalScript = (scriptURL, scriptID, onload = () => {}) => {
  const existingScript = document.getElementById(scriptID);
  if (existingScript && existingScript.loaded) {
    onload();
    return;
  }
  const script = existingScript || document.createElement('script');
  if (script.id !== scriptID && script.src !== scriptURL) {
    script.id = scriptID;
    script.src = scriptURL;
    script.async = true;
    script.loaded = false;
  }
  const previousOnLoad = script.onload;
  script.onload = () => {
    script.loaded = true;
    if (previousOnLoad) previousOnLoad();
    onload();
  };
  if (!existingScript) document.body.appendChild(script);
};

export const handleScroll = manager => {
  const offset = manager.header ? 0 - manager.header.clientHeight : 0;
  const currentSection = Object.entries(manager.sections)
    .filter(item => {
      const ref = item[1];
      return !!ref && window.scrollY >= ref.offsetTop + offset - 1;
    })
    .pop();
  const { activeSection } = manager;
  manager.activeSection = currentSection ? currentSection[0] : '';
  if (activeSection !== manager.activeSection) {
    const hash = manager.activeSection ? `#${manager.activeSection}` : ' ';
    // FIXME - use router
    // eslint-disable-next-line
    history.replaceState(null, null, hash);
  }
};

export const scrollHash = () => {
  const hash = window.location.hash.substring(1);
  if (!hash) return;
  window.requestAnimationFrame(() => {
    const element = document.getElementById(hash);
    if (element) element.scrollIntoView();
  });
};

// FIXME
// eslint-disable-next-line
const constructRouteParams = function(contentObj, contentSubPath, params = {}) {
  let route = '';
  let vars = {};
  try {
    const space = contentObj.space || contentObj.inSpace;
    if (space.spaceType === 'PROFILE') {
      vars = { profile: `@${space.content.slug}` };
      route = `profile.${contentSubPath}`;
    } else if (space.spaceType === 'INITIATIVE') {
      vars = { initiative: `+${space.content.slug}` };
      route = `initiative.${contentSubPath}`;
    } else if (space.spaceType === 'PROJECT') {
      route = `${space.parent.spaceType.toLowerCase()}.project.${contentSubPath}`;
      let parentSlug;
      if (space.parent.spaceType === 'INITIATIVE') {
        parentSlug = `+${space.parent.content.slug}`;
      } else if (space.parent.spaceType === 'PROFILE') {
        parentSlug = `@${space.parent.content.slug}`;
      }
      vars = {
        [space.parent.spaceType.toLowerCase()]: `${parentSlug}`,
      };
    }
    if (Object.keys(params).length) {
      Object.assign(vars, params);
    } else {
      Object.assign(vars, { slug: contentObj.slug });
    }
  } catch (e) {
    throw new Error(`You must return the following gql fields under the content object \
      {slug, space { spaceType, content { slug } } ${e}`);
  }
  return [route, vars];
};

// FIXME
// eslint-disable-next-line
export const redirectToContent = function(
  contentObj,
  contentSubPath,
  params = {},
) {
  try {
    const [route, vars] = constructRouteParams(
      contentObj,
      contentSubPath,
      params,
    );
    routerStore.router.navigate(route, vars);
  } catch (error) {
    analytics.logError(error);
    Alert.error('Hmm... Something went wrong!');
  }
};

export const ContentLink = observer(
  ({ contentObj, contentSubPath, params = {}, children, content }) => {
    const [route, vars] = constructRouteParams(
      contentObj,
      contentSubPath,
      params,
    );
    return (
      <Link name={route} params={vars} content={content}>
        {children}
      </Link>
    );
  },
);

// eslint-disable-next-line
export const raiseUserError = function(error, action, contentType) {
  if (error[0].code === 'AUTHORISATION') {
    Alert.error(
      `Sorry but you're not authorised to ${action} this ${contentType}.`,
    );
  } else {
    Alert.error(
      `Hmm... We failed to ${action} this ${contentType}. Please try again.`,
    );
  }
};

// eslint-disable-next-line
export const mapToObject = function(aMap) {
  const obj = {};
  aMap.forEach((v, k) => {
    obj[k] = v;
  });
  return obj;
};

// eslint-disable-next-line
export const stripTags = function(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, '');
};

const legacyClipboardCopy = value => {
  // Create new element
  const el = document.createElement('textarea');
  // Set value (string to be copied)
  el.value = value;
  // Set non-editable to avoid focus and move outside of view
  el.setAttribute('readonly', '');
  el.style = { position: 'absolute', left: '-9999px' };
  document.body.appendChild(el);
  // Select text inside element
  el.select();
  // Copy text to clipboard
  document.execCommand('copy');
  // Remove temporary element
  document.body.removeChild(el);
};

const clipboardAPICopy = async value => {
  // https://web.dev/async-clipboard/
  try {
    await navigator.clipboard.writeText(value);
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

export const clipboardCopy = (value = '') => {
  if (!navigator.clipboard) {
    legacyClipboardCopy(value);
    return;
  }

  clipboardAPICopy(value);
};

export const getCurrencyFormat = (currency, locale = 'en-US') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
};

export const addQuotes = text =>
  typeof text === 'string' ? `“${text}”` : text;
